/**
 * Challenges section for level pages.
 *
 * What: Highlights common pain points students face at a specific level.
 * Why: Demonstrates understanding of student/parent concerns and builds trust.
 * When: Update when changing challenges section structure or layout.
 */
import { Paper, Stack, Typography } from '@mui/material';

import LandingCardGrid from '@/components/landing/LandingCardGrid';
import LandingCenteredSectionHead from '@/components/landing/LandingCenteredSectionHead';
import LandingSection from '@/components/landing/LandingSection';
import { colors, radii, shadows } from '@/components/landing/landingTokens';

interface ChallengeItem {
  title: string;
  description: string;
}

export interface ChallengesSectionContent {
  title: string;
  body?: string;
  items: ChallengeItem[];
}

interface ChallengesSectionProps {
  content: ChallengesSectionContent;
}

function ChallengesSection({ content }: ChallengesSectionProps) {
  const titleId = 'challenges-title';

  return (
    <LandingSection id="challenges" tone="muted" ariaLabelledby={titleId}>
      <Stack
        spacing={0}
        sx={{
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}
      >
        <LandingCenteredSectionHead
          titleId={titleId}
          title={content.title}
          body={content.body}
        />

        <LandingCardGrid columns={{ xs: 1, md: 2 }}>
          {content.items.map((item, idx) => (
            <Paper
              key={idx}
              elevation={0}
              sx={{
                width: '100%',
                minWidth: 0,
                boxSizing: 'border-box',
                height: '100%',
                p: { xs: 3, sm: 3.5, md: 4 },
                borderRadius: radii.md,
                backgroundColor: colors.paper,
                border: `1px solid ${colors.border}`,
                boxShadow: shadows.card,
              }}
            >
              <Stack spacing={1.5} sx={{ width: '100%', minWidth: 0 }}>
                <Typography
                  component="h3"
                  sx={{
                    fontSize: { xs: 17, sm: 18, md: 19 },
                    fontWeight: 700,
                    color: colors.ink,
                    lineHeight: 1.3,
                    overflowWrap: 'break-word',
                  }}
                >
                  {item.title}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: colors.inkMuted,
                    lineHeight: 1.65,
                    fontSize: { xs: 15, sm: 16 },
                    overflowWrap: 'break-word',
                  }}
                >
                  {item.description}
                </Typography>
              </Stack>
            </Paper>
          ))}
        </LandingCardGrid>
      </Stack>
    </LandingSection>
  );
}

export default ChallengesSection;
